import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Criterion } from '@/types/evaluation.types';
import { Importance } from '@/types/evaluation.types';
import { generateId } from '@/lib/id';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2, Plus, AlertCircle, Star, AlertTriangle, Info, Minus } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const importanceIcons = {
  [Importance.Critical]: <Star className="h-3 w-3 text-red-600" aria-hidden="true" />,
  [Importance.High]: <AlertTriangle className="h-3 w-3 text-orange-600" aria-hidden="true" />,
  [Importance.Medium]: <AlertCircle className="h-3 w-3 text-yellow-600" aria-hidden="true" />,
  [Importance.Low]: <Info className="h-3 w-3 text-blue-600" aria-hidden="true" />,
  [Importance.None]: <Minus className="h-3 w-3 text-gray-400" aria-hidden="true" />,
};

interface BulkCriterionEntryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (criteria: Criterion[]) => void;
}

interface CriterionRow {
  id: string;
  title: string;
  description: string;
  importance: Importance;
  errors?: {
    title?: string;
    description?: string;
  };
}

export function BulkCriterionEntry({
  open,
  onOpenChange,
  onSave,
}: BulkCriterionEntryProps) {
  const { t } = useTranslation(['criteria', 'common']);

  const createEmptyRow = (): CriterionRow => ({
    id: generateId(),
    title: '',
    description: '',
    importance: Importance.Medium,
  });

  const [rows, setRows] = useState<CriterionRow[]>([createEmptyRow()]);

  const handleAddRow = () => {
    setRows([...rows, createEmptyRow()]);
  };

  const handleRemoveRow = (id: string) => {
    if (rows.length === 1) {
      // Don't remove the last row, just clear it
      setRows([createEmptyRow()]);
    } else {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const handleUpdateRow = (id: string, field: keyof CriterionRow, value: string | Importance) => {
    setRows(
      rows.map((row) =>
        row.id === id
          ? { ...row, [field]: value, errors: { ...row.errors, [field]: undefined } }
          : row
      )
    );
  };

  const validateRows = (): boolean => {
    let isValid = true;
    const updatedRows = rows.map((row) => {
      const errors: CriterionRow['errors'] = {};

      if (!row.title.trim()) {
        errors.title = t('form.criterion.errors.titleRequired');
        isValid = false;
      } else if (row.title.length > 255) {
        errors.title = t('form.criterion.errors.titleTooLong', { max: 255 });
        isValid = false;
      }

      if (!row.description.trim()) {
        errors.description = t('form.criterion.errors.descriptionRequired');
        isValid = false;
      } else if (row.description.length > 1024) {
        errors.description = t('form.criterion.errors.descriptionTooLong', { max: 1024 });
        isValid = false;
      }

      return { ...row, errors: Object.keys(errors).length > 0 ? errors : undefined };
    });

    setRows(updatedRows);
    return isValid;
  };

  const handleSubmit = () => {
    // Filter out empty rows
    const filledRows = rows.filter((row) => row.title.trim() || row.description.trim());

    if (filledRows.length === 0) {
      onOpenChange(false);
      return;
    }

    // Update rows to only validate filled rows
    setRows(filledRows);

    if (!validateRows()) {
      return;
    }

    const criteria: Criterion[] = filledRows.map((row) => ({
      id: row.id,
      title: row.title.trim(),
      description: row.description.trim(),
      importance: row.importance,
      category: null,
      helpfulHints: undefined,
    }));

    onSave(criteria);

    // Reset form
    setRows([createEmptyRow()]);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setRows([createEmptyRow()]);
    onOpenChange(false);
  };

  const filledRowCount = rows.filter((row) => row.title.trim() || row.description.trim()).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('form.bulkEntry.title')}</DialogTitle>
          <DialogDescription>{t('form.bulkEntry.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-3">
          {/* Info Alert */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t('form.bulkEntry.hint')}</AlertDescription>
          </Alert>

          {/* Table Container */}
          <div className="flex-1 overflow-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-muted sticky top-0 z-10">
                <tr className="border-b">
                  <th className="text-left p-2 font-medium w-8">#</th>
                  <th className="text-left p-2 font-medium min-w-[200px]">
                    {t('form.criterion.title')} <span className="text-destructive">*</span>
                  </th>
                  <th className="text-left p-2 font-medium min-w-[300px]">
                    {t('form.criterion.description')} <span className="text-destructive">*</span>
                  </th>
                  <th className="text-left p-2 font-medium w-[140px]">
                    {t('form.importance.label')}
                  </th>
                  <th className="text-center p-2 font-medium w-12"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id} className="border-b hover:bg-muted/50">
                    <td className="p-2 text-muted-foreground text-center">
                      {index + 1}
                    </td>
                    <td className="p-2">
                      <Input
                        value={row.title}
                        onChange={(e) => handleUpdateRow(row.id, 'title', e.target.value)}
                        placeholder={t('form.criterion.titlePlaceholder')}
                        aria-invalid={!!row.errors?.title}
                        aria-label={`${t('form.criterion.title')} ${index + 1}`}
                        maxLength={255}
                        className={`h-8 ${row.errors?.title ? 'border-destructive' : ''}`}
                      />
                      {row.errors?.title && (
                        <p className="text-xs text-destructive mt-1" role="alert">
                          {row.errors.title}
                        </p>
                      )}
                    </td>
                    <td className="p-2">
                      <Input
                        value={row.description}
                        onChange={(e) => handleUpdateRow(row.id, 'description', e.target.value)}
                        placeholder={t('form.criterion.descriptionPlaceholder')}
                        aria-invalid={!!row.errors?.description}
                        aria-label={`${t('form.criterion.description')} ${index + 1}`}
                        maxLength={1024}
                        className={`h-8 ${row.errors?.description ? 'border-destructive' : ''}`}
                      />
                      {row.errors?.description && (
                        <p className="text-xs text-destructive mt-1" role="alert">
                          {row.errors.description}
                        </p>
                      )}
                    </td>
                    <td className="p-2">
                      <Select
                        value={row.importance}
                        onValueChange={(value) =>
                          handleUpdateRow(row.id, 'importance', value as Importance)
                        }
                      >
                        <SelectTrigger
                          className="h-8 text-xs"
                          aria-label={`${t('form.importance.label')} ${index + 1}`}
                        >
                          <SelectValue>
                            <div className="flex items-center gap-2">
                              {importanceIcons[row.importance]}
                              <span>{t(`form.importance.levels.${row.importance.toLowerCase()}`)}</span>
                            </div>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.values(Importance).map((importance) => (
                            <SelectItem key={importance} value={importance}>
                              <div className="flex items-center gap-2">
                                {importanceIcons[importance]}
                                <span>{t(`form.importance.levels.${importance.toLowerCase()}`)}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-2 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveRow(row.id)}
                        aria-label={`${t('common:actions.delete')} ${index + 1}`}
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add Row Button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddRow}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
            {t('form.bulkEntry.addRow')}
          </Button>
        </div>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <span className="text-sm text-muted-foreground">
              {t('form.bulkEntry.criteriaCount', { count: filledRowCount })}
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                {t('common:actions.cancel')}
              </Button>
              <Button onClick={handleSubmit}>
                {t('form.bulkEntry.saveAll', { count: filledRowCount })}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
